// T001 — Social page analytics event definitions.

import type { EventDefinition } from './navigation';

// ---------------------------------------------------------------------------
// Social event property types
// ---------------------------------------------------------------------------

export type SocialTabSwitchedProperties = {
  tab: string;
  previousTab: string;
};

export type SocialThesisReactedProperties = {
  signature: string;
  action: 'up' | 'down' | 'cleared';
};

export type SocialThesisRepliedProperties = {
  signature: string;
};

export type SocialThesisSharedProperties = {
  signature: string;
  method: 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp';
};

export type SocialProposalSharedProperties = {
  proposalPubkey: string;
  method: 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp' | 'reddit';
};

export type SocialTradeSharedProperties = {
  signature: string;
  marketPubkey: string;
  direction: 'buy' | 'sell' | 'swap';
  method: 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp';
};

export type SocialBoostSharedProperties = {
  activityType: 'boost';
  marketPubkey: string;
  method: 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp';
};

export type SocialProposalBackSharedProperties = {
  proposalPubkey: string;
  outcomeIndex: number;
  method: 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp';
};

export type SocialThesisTradeClickedProperties = {
  signature: string;
  marketPubkey: string;
  source?: 'reply_gate';
};

export type SocialThesisReplyGateShownProperties = {
  marketPubkey: string;
  canTrade: boolean;
};

export type SocialReferralLinkCopiedProperties = {
  method: 'copy' | 'native' | 'x' | 'telegram' | 'whatsapp' | 'qr';
  /** Which surface the link was copied from. Lets the rewards cards reuse this
   *  shared event while keeping their copies separable from other referral surfaces. */
  source?: 'rewards_dashboard' | 'rewards_profile';
};

export type SocialReferralRankSharedProperties = {
  pnlRank: number;
  volumeRank: number;
};

export type SocialReferralRankBoostersSharedProperties = {
  amountRank: number;
  boostsRank: number;
  marketsBoostedRank: number;
};

export type SocialReferralRankCreatorsSharedProperties = {
  topMarketRank: number;
  marketsCreatedRank: number;
  totalVolumeRank: number;
};

export type SocialReferralRankBackersSharedProperties = {
  amountRank: number;
  backsRank: number;
  topMarketRank: number;
};

export type SocialCreatorClickedProperties = {
  creatorUserId: string;
};

export type SocialPageLoadedProperties = {
  tab: string;
  authenticated: boolean;
};

// ---------------------------------------------------------------------------
// Podium events (spec 071) — distinct from regular row clicks so rank-region
// click-through can be analysed independently.
// ---------------------------------------------------------------------------

export type SocialPodiumCardClickedProperties = {
  tab: 'leaderboard' | 'boosters' | 'creators' | 'backers';
  rank: 1 | 2 | 3;
  targetUserId: string;
  region: 'avatar' | 'name';
};

// ---------------------------------------------------------------------------
// Social event definitions
// ---------------------------------------------------------------------------

export const SOCIAL_TAB_SWITCHED: EventDefinition<SocialTabSwitchedProperties> = {
  name: 'social_tab_switched',
};

export const SOCIAL_THESIS_REACTED: EventDefinition<SocialThesisReactedProperties> = {
  name: 'social_thesis_reacted',
};

export const SOCIAL_THESIS_REPLIED: EventDefinition<SocialThesisRepliedProperties> = {
  name: 'social_thesis_replied',
};

export const SOCIAL_THESIS_SHARED: EventDefinition<SocialThesisSharedProperties> = {
  name: 'social_thesis_shared',
};

export const SOCIAL_PROPOSAL_SHARED: EventDefinition<SocialProposalSharedProperties> = {
  name: 'social_proposal_shared',
};

export const SOCIAL_TRADE_SHARED: EventDefinition<SocialTradeSharedProperties> = {
  name: 'social_trade_shared',
};

export const SOCIAL_BOOST_SHARED: EventDefinition<SocialBoostSharedProperties> = {
  name: 'social_boost_shared',
};

export const SOCIAL_PROPOSAL_BACK_SHARED: EventDefinition<SocialProposalBackSharedProperties> = {
  name: 'social_proposal_back_shared',
};

export const SOCIAL_THESIS_TRADE_CLICKED: EventDefinition<SocialThesisTradeClickedProperties> = {
  name: 'social_thesis_trade_clicked',
};

export const SOCIAL_THESIS_REPLY_GATE_SHOWN: EventDefinition<SocialThesisReplyGateShownProperties> =
  {
    name: 'social_thesis_reply_gate_shown',
  };

export const SOCIAL_REFERRAL_LINK_COPIED: EventDefinition<SocialReferralLinkCopiedProperties> = {
  name: 'social_referral_link_copied',
};

export const SOCIAL_REFERRAL_RANK_SHARED: EventDefinition<SocialReferralRankSharedProperties> = {
  name: 'social_referral_rank_shared',
};

export const SOCIAL_REFERRAL_RANK_BOOSTERS_SHARED: EventDefinition<SocialReferralRankBoostersSharedProperties> =
  {
    name: 'social_referral_rank_boosters_shared',
  };

export const SOCIAL_REFERRAL_RANK_CREATORS_SHARED: EventDefinition<SocialReferralRankCreatorsSharedProperties> =
  {
    name: 'social_referral_rank_creators_shared',
  };

export const SOCIAL_REFERRAL_RANK_BACKERS_SHARED: EventDefinition<SocialReferralRankBackersSharedProperties> =
  {
    name: 'social_referral_rank_backers_shared',
  };

export const SOCIAL_CREATOR_CLICKED: EventDefinition<SocialCreatorClickedProperties> = {
  name: 'social_creator_clicked',
};

export const SOCIAL_PAGE_LOADED: EventDefinition<SocialPageLoadedProperties> = {
  name: 'social_page_loaded',
};

export const SOCIAL_PODIUM_CARD_CLICKED: EventDefinition<SocialPodiumCardClickedProperties> = {
  name: 'social_podium_card_clicked',
};
